// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { Checkbox, Text } from 'informed';

import {
  addHemoglobinTest,
  deleteHemoglobinTest,
  updateHemoglobinTest,
  updateAdmissionHemoglobinTest,
} from '../../../actions/testingActions';

import DateText from '../../DateText.jsx';
import NumericText from '../../NumericText.jsx';
import Table from '../../Table.jsx';
import { Error } from '../../Typography.jsx';

type Props = {
  hemoglobinTests: Object[],
  admissionHemoglobinTest: Object,
  wellChildChecks: Object[],
  dob: string,
  dateArrived: string,
  activePatientId: string,
  addHemoglobinTest: Function,
  deleteHemoglobinTest: Function,
  updateHemoglobinTest: Function,
  updateAdmissionHemoglobinTest: Function,
}

class HemoglobinTestsComponent extends Component<Props> {
  LOW_THRESHOLD = 9;

  DATE_OF_ADMISSION_TEXT = 'Date of admission';

  FROM_WCC_TEXT = '(from WCC)';

  /* eslint-disable no-param-reassign */
  transformDates(formValues) {
    formValues.dateDue = moment(formValues.dateDue, 'DD-MM-YYYY').toISOString();
    formValues.dateAdministered = moment(formValues.dateAdministered, 'DD-MM-YYYY').toISOString();
  }

  handleDeleteRow = (id: string) => {
    this.props.deleteHemoglobinTest(this.props.activePatientId, id);
  }

  showDelete = (row: Object) => row.showDelete !== false;

  handleSaveRow = (id: string, formData: Object) => {
    if (formData.values.dateDue === this.DATE_OF_ADMISSION_TEXT) {
      this.transformDates(formData.values);
      this.props.updateAdmissionHemoglobinTest(this.props.activePatientId, {
        ...formData.values,
        dateDue: this.props.dateArrived,
      });
    } else {
      this.transformDates(formData.values);
      this.props.updateHemoglobinTest(this.props.activePatientId, {
        testId: id,
        ...formData.values,
      });
    }
  }

  showEdit = (row: Object) => row.showEdit !== false;

  handleSaveNewRow = (formData: Object) => {
    this.transformDates(formData.values);
    this.props.addHemoglobinTest(this.props.activePatientId, formData.values);
  }

  // overdue if no administered date and exists due date earlier than now
  isOverdue = item => (!item.dateAdministered
    && !item.overridden
    && item.dateDue
    && moment(item.dateDue) < moment())

  render() {
    const { hemoglobinTests, admissionHemoglobinTest } = this.props;
    const { dateArrived } = this.props;
    let allHemoglobinTests = [...hemoglobinTests];
    for (let i = 0; i < this.props.wellChildChecks.length; i++) {
      const wcc = this.props.wellChildChecks[i];
      if (wcc.vitals.hgb) {
        allHemoglobinTests.push({
          dateDue: this.FROM_WCC_TEXT,
          dateAdministered: wcc.date,
          result: wcc.vitals.hgb,
          takeIron: wcc.vitals.hgbIron,
          notes: wcc.notes,
          editors: wcc.editors,
          editDates: wcc.editDates,
          showDelete: false,
          showEdit: false,
        });
      }
    }

    allHemoglobinTests.push({
      ...admissionHemoglobinTest,
      admissionTest: true,
      showDelete: false,
      dateDue: dateArrived,
    });

    allHemoglobinTests.sort((a, b) => {
      // overdues go first
      const aOverdue = this.isOverdue(a);
      const bOverdue = this.isOverdue(b);
      if (!aOverdue && bOverdue) {
        return 1;
      }
      if (!bOverdue && aOverdue) {
        return -1;
      }

      // otherwise based on date due or date administered if date due DNE
      let aDate = a.dateDue;
      if ((!aDate || aDate === this.FROM_WCC_TEXT) && !a.overridden) {
        aDate = a.dateAdministered;
      }
      let bDate = b.dateDue;
      if ((!bDate || bDate === this.FROM_WCC_TEXT) && !b.overridden) {
        bDate = b.dateAdministered;
      }
      if (!aDate) {
        return 1;
      }
      if (!bDate) {
        return -1;
      }
      if (aDate < bDate) {
        return 1;
      }
      if (bDate < aDate) {
        return -1;
      }
      return 0;
    });

    allHemoglobinTests = allHemoglobinTests.map((item, index) => ({
      testNum: allHemoglobinTests.length - index, ...item,
    }));

    const indexCol = {
      Header: '',
      id: 'index',
      cell: item => `Test #${item.testNum ? item.testNum : ''}`,
      width: 80,
    };

    const resultCol = {
      Header: 'Result',
      id: 'result',
      cell: (item) => {
        // handle 0 separately due to its evaluation to false
        if (item.result === 0) {
          return <Error>0 g/dL (L)</Error>;
        }
        return (item.result && item.result < this.LOW_THRESHOLD
          ? <Error>{`${item.result} g/dL (L)`}</Error>
          : `${item.result || '--'} g/dL`);
      },
      editCell: item => <span>
        <NumericText field='result' initialValue={item.result} size={4} /> g/dL
      </span>,
    };

    const ironCol = {
      Header: 'For Hb Recheck',
      id: 'takeIron',
      cell: item => (item.takeIron === true ? '✓' : '--'),
      editCell: item => <Checkbox field='takeIron' initialValue={item.takeIron} />,
    };

    const dateDueCol = {
      Header: 'Date Due',
      id: 'dateDue',
      cell: (item) => {
        if (item.dateDue) {
          if (item.dateDue === this.FROM_WCC_TEXT) {
            return item.dateDue;
          }
          return moment(item.dateDue).format();
        }
        return '--';
      },
      editCell: item => (item.admissionTest
        ? <Text field='dateDue' initialValue={this.DATE_OF_ADMISSION_TEXT} disabled />
        : <DateText field='dateDue' item={item} />),
    };

    const dateAdministeredCol = {
      Header: 'Date Administered',
      id: 'dateAdministered',
      cell: (item) => {
        if (item.overridden) {
          return '(overridden)';
        }
        const overdue = this.isOverdue(item);
        return overdue ? <Error>-- (overdue)</Error>
          : item.dateAdministered ? moment(item.dateAdministered).format() : '--';
      },
      editCell: item => <div>
        <DateText field='dateAdministered' item={item} />
        <br />
        <Checkbox field='overridden' initialValue={item.overridden} />Override
      </div>,
    };

    const columns = [indexCol, dateDueCol, dateAdministeredCol, resultCol, ironCol];

    return (
      <div>
        <Table
          columns={columns}
          data={allHemoglobinTests}
          handleDeleteRow={this.handleDeleteRow}
          showDelete={this.showDelete}
          showEdit={this.showEdit}
          handleSaveRow={this.handleSaveRow}
          handleSaveNewRow={this.handleSaveNewRow}
        />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  hemoglobinTests: state.patient.data.testing.hemoglobinTests,
  admissionHemoglobinTest: state.patient.data.testing.admissionHemoglobinTest,
  wellChildChecks: state.patient.data.wellChildChecks,
  dob: state.patient.data.generalInfo.dob,
  dateArrived: state.patient.data.generalInfo.dateArrived,
  activePatientId: state.patient.data.activePatientId,
});

const mapDispatchToProps = dispatch => ({
  addHemoglobinTest: (patientId, hemoglobinTest) => dispatch(
    addHemoglobinTest(patientId, hemoglobinTest),
  ),
  deleteHemoglobinTest: (patientId, hemoglobinTestId) => dispatch(
    deleteHemoglobinTest(patientId, hemoglobinTestId),
  ),
  updateHemoglobinTest: (patientId, hemoglobinTest) => dispatch(
    updateHemoglobinTest(patientId, hemoglobinTest),
  ),
  updateAdmissionHemoglobinTest: (patientId, hemoglobinTest) => dispatch(
    updateAdmissionHemoglobinTest(patientId, hemoglobinTest),
  ),
});

const HemoglobinTests = connect(
  mapStateToProps,
  mapDispatchToProps,
)(HemoglobinTestsComponent);

export default HemoglobinTests;



// WEBPACK FOOTER //
// ./client/src/components/patientPages/testing/HemoglobinTests.jsx